import React, { FC, useCallback, useMemo, memo } from 'react';
import { useFormikContext } from 'formik';
import _some from 'lodash/some';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _includes from 'lodash/includes';
import { gqlTalentUnionEnum as TALENT_UNION } from '@minecraft.graphql-types';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import { useUnionStatusOptions } from '@blocs.filter';
import { sagAftraChildrenExclusion } from '../../../shared/config';
import { FilterData, SelectOption } from '../../../shared/types';
import { shouldNonUnionBeChecked } from '../../../shared/utils';

type Props = {
  unionStatusName: FilterData['name'];
  excludeUnionStatusName: FilterData['name'];
  label: FilterData['label'];
  className?: string;
};

const UnionStatus: FC<Props> = ({ unionStatusName, excludeUnionStatusName, label, className }) => {
  const { values, setFieldValue } = useFormikContext();
  const unionStatusOptions = useUnionStatusOptions();

  const handleUnionStatusChange = useCallback(
    (options: SelectOption[]): void => {
      const isNonUnionChecked = shouldNonUnionBeChecked(options);
      const isSomeSagAftraChildrenExclusionChecked = _some(options, (option) =>
        _includes(sagAftraChildrenExclusion, option.value)
      );
      const nonUnionStatus = _find(unionStatusOptions, { value: TALENT_UNION.NON_UNION });
      const unionStatusOptionsWithoutNonUnionStatus = _filter(
        unionStatusOptions,
        (item) => item.value !== TALENT_UNION.NON_UNION
      );

      if (isNonUnionChecked && isSomeSagAftraChildrenExclusionChecked) {
        const checkedSagAftraChildrenExclusion = _filter(options, (option) =>
          _includes(sagAftraChildrenExclusion, option.value)
        );
        const checkedSagAftraChildrenExclusionValues = _map(checkedSagAftraChildrenExclusion, (option) => option.value);

        setFieldValue(unionStatusName, [nonUnionStatus, ...checkedSagAftraChildrenExclusion], false);
        setFieldValue(
          excludeUnionStatusName,
          _filter(
            unionStatusOptionsWithoutNonUnionStatus,
            (option) => !_includes(checkedSagAftraChildrenExclusionValues, option.value)
          ),
          false
        );
      } else if (isNonUnionChecked) {
        setFieldValue(unionStatusName, [nonUnionStatus], false);
        setFieldValue(excludeUnionStatusName, unionStatusOptionsWithoutNonUnionStatus, false);
      } else {
        setFieldValue(excludeUnionStatusName, [], false);
        setFieldValue(unionStatusName, options, false);
      }
    },
    [values[unionStatusName]]
  );

  const disabledOptions = useMemo(() => {
    if (shouldNonUnionBeChecked(values[unionStatusName])) {
      return _filter(
        values[excludeUnionStatusName],
        (item) => !_includes([...sagAftraChildrenExclusion, TALENT_UNION.NON_UNION], item.value)
      );
    }

    return [];
  }, [values[unionStatusName], values[excludeUnionStatusName]]);

  return (
    <DropdownWithChildren
      isMultipleSelect
      toggleLabel={label}
      options={unionStatusOptions}
      initialSelectedOptions={values[unionStatusName]}
      initialDisabledOptions={disabledOptions}
      onOptionsChange={handleUnionStatusChange}
      className={className}
    />
  );
};

export default memo(UnionStatus);
